import React from 'react';
import { Switch, styled } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import { setThemeMode } from '@/features/userPrefs/userPrefsSlice';

const iconSx = { fontSize: 14, color: 'primary.main' };
const checkedIconSx = { fontSize: 14, color: 'common.white' };

// Supplying a custom `icon`/`checkedIcon` to Switch replaces its default thumb
// entirely, so we recreate the thumb ourselves (same class name the styles below
// target) and center the icon inside it.
const Thumb = ({ children }) => <span className='MuiSwitch-thumb'>{children}</span>;

const StyledSwitch = styled((props) => (
	<Switch
		focusVisibleClassName='.Mui-focusVisible'
		disableRipple
		icon={
			<Thumb>
				<DarkModeOutlinedIcon sx={{ ...iconSx }} />
			</Thumb>
		}
		checkedIcon={
			<Thumb>
				<LightModeOutlinedIcon sx={{ ...checkedIconSx }} />
			</Thumb>
		}
		{...props}
	/>
))(({ theme }) => ({
	width: 42,
	height: 26,
	padding: 0,
	'& .MuiSwitch-switchBase': {
		padding: 0,
		margin: 2,
		transitionDuration: '300ms',
		'&.Mui-checked': {
			transform: 'translateX(16px)',
			color: '#fff',
			'& .MuiSwitch-thumb': {
				backgroundColor: theme.palette.primary.main,
			},
			'& + .MuiSwitch-track': {
				backgroundColor: theme.palette.primary.main,
				opacity: 1,
				border: 0,
				...theme.applyStyles('dark', {
					backgroundColor: theme.palette.primary.dark,
				}),
			},
			'&.Mui-disabled + .MuiSwitch-track': {
				opacity: 0.5,
			},
		},
		'&.Mui-focusVisible .MuiSwitch-thumb': {
			color: theme.palette.primary.main,
			border: '6px solid #fff',
		},
		'&.Mui-disabled .MuiSwitch-thumb': {
			color: theme.palette.grey[100],
			...theme.applyStyles('dark', {
				color: theme.palette.grey[600],
			}),
		},
		'&.Mui-disabled + .MuiSwitch-track': {
			opacity: 0.7,
			...theme.applyStyles('dark', {
				opacity: 0.3,
			}),
		},
	},
	'& .MuiSwitch-thumb': {
		boxSizing: 'border-box',
		width: 22,
		height: 22,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: '50%',
		backgroundColor: theme.palette.common.white,
	},
	'& .MuiSwitch-track': {
		borderRadius: 26 / 2,
		backgroundColor: theme.palette.grey[300],
		opacity: 1,
		transition: theme.transitions.create(['background-color'], {
			duration: 500,
		}),
		...theme.applyStyles('dark', {
			backgroundColor: theme.palette.grey[700],
		}),
	},
}));

export const DarkModeSwitch = () => {
	const dispatch = useDispatch();
	const isDarkMode = useSelector((state) => state.userPrefs.themeMode === 'dark');

	const handleToggleDarkMode = () => dispatch(setThemeMode(isDarkMode ? 'light' : 'dark'));

	return (
		<StyledSwitch
			checked={isDarkMode}
			onClick={(e) => e.stopPropagation()}
			onChange={handleToggleDarkMode}
		/>
	);
};
